import type { PortfolioChatDto } from "@/api/types";
import { ChatPreview } from "@/components/ChatPreview/ChatPreview";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { LogOut01Icon } from "@/components/Icons/LogOut01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Badge } from "@/design-system/Badge";
import { Stack } from "@/design-system/Stack";
import { portfolioChatMutations } from "@/queries/portfolio-chat/mutations";
import { useTranslation } from "@/translations";

interface PortfolioChatListItemProps {
  chat: PortfolioChatDto;
  onOpen: () => void;
}

export function PortfolioChatListItem({ chat, onOpen }: PortfolioChatListItemProps): React.ReactNode {
  const { t } = useTranslation();
  const deleteChatMutation = portfolioChatMutations.useDeleteChat();
  const leaveGroupChatMutation = portfolioChatMutations.useLeaveGroupChat();

  const isChatGroupChat = chat.chatType === "group";
  const isLeaveActionAvailable = isChatGroupChat && chat.groupChatInfo?.isMember && chat.groupChatInfo?.canLeave;
  const isDeleteActionAvailable = !isChatGroupChat || !chat.groupChatInfo?.isMember;

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.leave"),
      },
      value: "leave",
      "data-testid": "context-menu-leave-btn",
      icon: <LogOut01Icon />,
      callbackFn: () => leaveGroupChatMutation.mutate(chat.id),
      isHidden: !isLeaveActionAvailable,
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      "data-testid": "context-menu-delete-btn",
      icon: <Trash02Icon />,
      callbackFn: () => deleteChatMutation.mutate(chat.id),
      isHidden: !isDeleteActionAvailable,
    },
  ];

  return (
    <li
      className="flex w-full items-start gap-space-md border-b border-b-border-default bg-background-default transition-colors last:border-b-0 hover:bg-background-secondary"
      data-testid="portfolio-chat-list-item"
    >
      <button
        onClick={onOpen}
        type="button"
        className="flex min-w-0 flex-1 cursor-pointer flex-col items-stretch p-space-md pr-0 text-left whitespace-normal text-text-primary focus-visible:ring-2 focus-visible:ring-border-brand focus-visible:outline-hidden"
      >
        <Stack gap="md">
          <ChatPreview chat={chat} showLatestReplyDate showUnreadRepliesCount />
          {chat.project && (
            <Badge data-testid="portfolio-chat-project-label" icon={<Building01Icon />}>
              {chat.project.name}
            </Badge>
          )}
        </Stack>
      </button>
      <aside className="flex h-full flex-col items-start py-space-md pr-space-md">
        <nav className="flex min-h-control-md items-center">
          <ContextMenu items={menuActions} />
        </nav>
      </aside>
    </li>
  );
}
